import { CollectionViewer } from '@angular/cdk/collections';
import {
  AgrDataSource,
  ExportableDataSource,
  PaginatedDataSource,
  PaginationModel,
  SortableDataSource,
  Sorting,
} from '@next/core-lib/table-v2';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  map,
  shareReplay,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { toOfferExportModel } from 'src/app/offer/models/manure-offer.model';
import { OfferService } from 'src/app/shared/services/offers/offers.service';

import { offerMakeMatchSuccess } from '../../overview-slide-in/offers/store/offers-overview-slide.actions';
import { OfferOverviewModel, OrderOptions } from '../models/overview.model';
import { PageAndSize } from '../models/paginated-overview.model';
import * as OverviewActions from '../store/actions/overview.actions';
import * as OverviewSelectors from '../store/selectors/overview.selector';

/* eslint-disable brace-style */
export class OfferOverviewDataSource
  extends AgrDataSource<OfferOverviewModel>
  implements
    PaginatedDataSource<OfferOverviewModel>,
    SortableDataSource<OfferOverviewModel>,
    ExportableDataSource<OfferOverviewModel>
{
  public data$ = new BehaviorSubject<OfferOverviewModel[]>([]);
  offers$ = this.store.select(OverviewSelectors.selectOfferList);
  totalNumberOfOffers$ = this.store.select(OverviewSelectors.getTotalNumberOfOffers);
  readonly _sortingSubject$ = new BehaviorSubject<Sorting>({});

  readonly orderOptions$: Observable<OrderOptions> = this._sortingSubject$.pipe(
    map((sorting) => {
      const sortColumn = Object.keys(sorting)[0];
      const sortDirection = sorting[sortColumn];

      return sortColumn && sortDirection
        ? { column: sortColumn, descending: sortDirection === 'desc' }
        : undefined;
    }),
  );

  private pageAndSizeSubject$ = new BehaviorSubject<PageAndSize>({
    pageIndex: 0,
    pageSize: 25,
  });
  private readonly destroyPaginatorSubject$ = new Subject<void>();
  private readonly destroySubject$ = new Subject<boolean>();

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly offerService: OfferService,
  ) {
    super();
    combineLatest([this.pageAndSizeSubject$])
      .pipe(debounceTime(100), takeUntil(this.destroySubject$))
      .subscribe(([paginationOptions]) => {
        this.store.dispatch(OverviewActions.loadOffers({ paginationOptions }));
      });

    this.actions$
      .pipe(
        ofType(offerMakeMatchSuccess),
        withLatestFrom(this.pageAndSizeSubject$),
        takeUntil(this.destroySubject$),
        tap(([_, pageAndSize]) => {
          this.pageAndSizeSubject$.next(pageAndSize);
        }),
      )
      .subscribe();
  }

  prepareExportData<OfferExportModel>() {
    return this.offerService
      .getOffersForExport()
      .pipe(
        map((response) => response.offers.map((offer) => toOfferExportModel(offer))),
      ) as Observable<OfferExportModel[]>;
  }

  connect(_: CollectionViewer): Observable<OfferOverviewModel[]> {
    this.store
      .select(OverviewSelectors.selectOverviewOfferLoading)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((loading) => {
        this.loadingSubject.next(loading);
      });

    combineLatest([this.offers$, this.orderOptions$])
      .pipe(debounceTime(100), takeUntil(this.destroySubject$), shareReplay(1))
      .subscribe(([offers, order]) => {
        let sortedOffers = offers;

        if (order && order.column) {
          sortedOffers = this.sortOffers(offers, order.column, order.descending);
        }

        this.data$.next(sortedOffers);
      });
    return this.data$;
  }

  disconnect(collectionViewer: CollectionViewer): void {
    super.disconnect(collectionViewer);
    this.destroySubject$.next(true);
    this.destroySubject$.complete();
  }

  connectPaginator(): Observable<PaginationModel> {
    return combineLatest([this.pageAndSizeSubject$, this.totalNumberOfOffers$]).pipe(
      takeUntil(this.destroyPaginatorSubject$),
      map(([{ pageIndex, pageSize }, totalNumberOfOffers]) => ({
        pageSize,
        pageIndex,
        length: totalNumberOfOffers,
      })),
    );
  }

  disconnectPaginator(): void {
    if (!this.destroyPaginatorSubject$.isStopped) {
      this.destroyPaginatorSubject$.next();
      this.destroyPaginatorSubject$.complete();
    }
  }

  applySorting(sorting: Sorting): void {
    this._sortingSubject$.next(sorting);
  }

  applyPage(pageIndex: number, pageSize: number): void {
    this.pageAndSizeSubject$.next({ pageIndex, pageSize });
  }

  sortOffers(
    offers: OfferOverviewModel[],
    clickedColumn: string,
    descending: boolean,
  ): OfferOverviewModel[] {
    const sortedOffers = [...offers];
    sortedOffers.sort((a, b) => {
      const property = clickedColumn as keyof OfferOverviewModel;
      const valueA = a[property];
      const valueB = b[property];

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return descending ? valueB.localeCompare(valueA) : valueA.localeCompare(valueB);
      }
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return descending ? valueB - valueA : valueA - valueB;
      }
      if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
        return descending ? (valueA ? 1 : -1) : valueA ? -1 : 1;
      }
      return 0;
    });
    return sortedOffers;
  }
}
